import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';

// Define a generic record type for form data
type FormDataRecord = Record<string, unknown>;

/**
 * Syncs TanStack Form state (values and errors) to React Hook Form
 * for shadcn/ui component compatibility.
 *
 * @param tanstackForm - The TanStack Form instance
 * @param rhfForm - The React Hook Form instance
 * @param fieldNames - Array of field names to sync
 */
export function useSyncTanstackToRHF<
  TFormData extends FormDataRecord = FormDataRecord,
>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tanstackForm: any, // Use any for TanStack form to avoid complex type compatibility issues
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rhfForm: UseFormReturn<any>,
  fieldNames: (keyof TFormData)[]
) {
  useEffect(() => {
    const syncFormState = () => {
      // Sync values
      fieldNames.forEach((fieldName) => {
        const value = tanstackForm.getFieldValue(fieldName);
        rhfForm.setValue(fieldName as string, value || '');
      });

      // Clear all errors first
      rhfForm.clearErrors();

      // Sync errors from TanStack Form
      fieldNames.forEach((fieldName) => {
        const fieldMeta = tanstackForm.getFieldMeta(fieldName);
        const errors = fieldMeta?.errors;

        if (errors?.length) {
          const errorMessages = errors.map((error: unknown) => {
            if (typeof error === 'string') {
              return error;
            } else if (
              error &&
              typeof error === 'object' &&
              'message' in error
            ) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              return String((error as any).message);
            } else {
              return String(error);
            }
          });

          rhfForm.setError(fieldName as string, {
            type: 'manual',
            message: errorMessages.join(', '),
          });
        }
      });
    };

    // Initial sync
    syncFormState();

    const subscription = tanstackForm.store.subscribe(syncFormState);

    return subscription;
  }, [tanstackForm, rhfForm, fieldNames]);
}
